import { Asset, AssetSearchResponse } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { ApiErrorDisplay } from '@/types/apiError';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { SectionHeader } from '../AssetsPage/SectionHeader';
import { SelectAsset } from './components/SelectAsset';
import { SuitableSubstitutesTable } from './SuitableSubstitutesTable';
import { SuitableSubstitutesFooter } from './SuitableSubstitutesFooter';
import { useTableState } from './hooks/useTableState';

export const SuitableSubstitutesContent = ({
  isLoading,
  error,
  searchData,
}: {
  isLoading: boolean;
  error?: ApiErrorDisplay;
  searchData?: AssetSearchResponse;
}) => {
  const { assetId, searchQuery, updateSearchQuery, updateSearchParameters } = useSearchFilters();
  const { asset: selectedAsset } = useGetAsset({ assetId });

  const tableState = useTableState(searchQuery, updateSearchQuery);
  const { hiddenAssetIds, setHiddenAssetIds } = tableState;

  console.log('hiddenAssetIds', hiddenAssetIds);

  const matches = useMemo(() => {
    if (!searchData?.matches) return [];

    return searchData.matches.map(match => ({
      ...match.asset,
      contentType: 'asset' as const,
      matchPercentage: 'matchPercentage' in match ? match.matchPercentage : undefined,
      forwardMatchPercentage: 'forwardMatchPercentage' in match ? match.forwardMatchPercentage : undefined,
      reverseMatchPercentage: 'reverseMatchPercentage' in match ? match.reverseMatchPercentage : undefined,
    }));
  }, [searchData]);

  const allAssets = selectedAsset ? [selectedAsset, ...matches] : matches;

  const handleAssetSelection = (asset: Asset) => {
    updateSearchParameters({
      assetId: asset.id,
      searchType: 'visual',
      sizeThreshold: 0,
    });
  };

  const handleUnhideAsset = (assetId: string) => {
    setHiddenAssetIds(previous => {
      const newSet = new Set(previous);
      newSet.delete(assetId);
      return newSet;
    });
  };

  const handleUnhideAll = () => {
    setHiddenAssetIds(new Set());
  };

  return (
    <>
      <SectionHeader>
        <Box display='flex' alignItems='center'>
          <BodyText>Suitable Substitutes</BodyText>
          <SelectAsset onAssetSelect={handleAssetSelection} />
        </Box>
      </SectionHeader>
      {(matches ?? []).length > 0 && (
        <>
          <SuitableSubstitutesTable
            isLoading={isLoading}
            error={error}
            searchData={searchData}
            tableState={tableState}
          />
          <SuitableSubstitutesFooter
            matches={matches}
            hiddenAssetIds={hiddenAssetIds}
            allAssets={allAssets}
            selectedAsset={selectedAsset}
            onUnhideAsset={handleUnhideAsset}
            onUnhideAll={handleUnhideAll}
          />
        </>
      )}
    </>
  );
};
